import React, {
    useEffect,
    useMemo,
    useState
} from "react";
import Select from "react-select";
import {
    useForm
} from "react-hook-form";

import {
    yupResolver
} from "@hookform/resolvers/yup";

import * as Yup from "yup";

import {
    useCreateQAQuestionMutation,
    useUpdateQAQuestionMutation,
    useGetAllQASectionsMutation
} from "./Api";

import ToastrService from "../../services/toastService";
import Loader from "../loader/loader";

const AddQAQuestion = ({
    mode = "add",
    data = null,
    selectedSectionId = null,
    onClose,
    onSave
}) => {

    const [
        createQAQuestionApi,
        {
            isLoading: createLoading
        }
    ] = useCreateQAQuestionMutation();

    const [
        updateQAQuestionApi,
        {
            isLoading: updateLoading
        }
    ] = useUpdateQAQuestionMutation();

    const [
        getAllQASectionsApi,
        {
            isLoading: sectionLoading
        }
    ] = useGetAllQASectionsMutation();

    const [
        sections,
        setSections
    ] = useState([]);

    const isEditMode =
        mode === "edit";

    const isViewMode =
        mode === "view";

    const isSubmitting =
        createLoading ||
        updateLoading;

    const isLoading =
        isSubmitting ||
        sectionLoading;

    const questionTypes = [
        {
            value: "yes_no",
            label: "Yes / No"
        },
        {
            value: "rating",
            label: "Rating"
        },
        {
            value: "critical_violation",
            label: "Critical Violation"
        }
    ];

    const normalizeBoolean = (
        value
    ) => {

        return (
            value === true ||
            value === 1 ||
            value === "1" ||
            value === "true"
        );

    };

    const normalizeSectionId = (
        value
    ) => {

        if (
            value === undefined ||
            value === null ||
            value === ""
        ) {
            return "";
        }

        return String(value);

    };

    const defaultFormValues =
        useMemo(
            () => ({
                sectionId:
                    normalizeSectionId(
                        selectedSectionId
                    ),

                questionText: "",

                questionType:
                    "yes_no",

                maximumScore: 5,

                displayOrder: "",

                status: "active",

                isMandatory: true,

                isCritical: false
            }),
            [
                selectedSectionId
            ]
        );

    const validationSchema =
        Yup.object().shape({

            sectionId:
                Yup.number()
                    .transform(
                        (
                            value,
                            originalValue
                        ) =>
                            originalValue === ""
                                ? undefined
                                : value
                    )
                    .typeError(
                        "Section is required"
                    )
                    .required(
                        "Section is required"
                    )
                    .integer(
                        "Invalid section"
                    )
                    .min(
                        1,
                        "Invalid section"
                    ),

            questionText:
                Yup.string()
                    .transform(
                        (value) =>
                            value?.trim()
                    )
                    .required(
                        "Question is required"
                    )
                    .min(
                        3,
                        "Question must contain at least 3 characters"
                    )
                    .max(
                        500,
                        "Question cannot exceed 500 characters"
                    ),

            questionType:
                Yup.string()
                    .required(
                        "Question type is required"
                    )
                    .oneOf(
                        questionTypes.map(
                            (item) =>
                                item.value
                        ),
                        "Invalid question type"
                    ),

            maximumScore:
                Yup.number()
                    .transform(
                        (
                            value,
                            originalValue
                        ) =>
                            originalValue === ""
                                ? undefined
                                : value
                    )
                    .typeError(
                        "Maximum score is required"
                    )
                    .required(
                        "Maximum score is required"
                    )
                    .min(
                        0,
                        "Maximum score cannot be negative"
                    )
                    .max(
                        100,
                        "Maximum score cannot exceed 100"
                    ),

            displayOrder:
                Yup.number()
                    .transform(
                        (
                            value,
                            originalValue
                        ) =>
                            originalValue === ""
                                ? undefined
                                : value
                    )
                    .typeError(
                        "Display order is required"
                    )
                    .required(
                        "Display order is required"
                    )
                    .integer(
                        "Display order must be a whole number"
                    )
                    .min(
                        1,
                        "Display order must be at least 1"
                    ),

            status:
                Yup.string()
                    .required(
                        "Status is required"
                    )
                    .oneOf(
                        [
                            "active",
                            "inactive"
                        ],
                        "Invalid status"
                    ),

            isMandatory:
                Yup.boolean(),

            isCritical:
                Yup.boolean()

        });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: {
            errors
        }
    } = useForm({

        resolver:
            yupResolver(
                validationSchema
            ),

        mode: "all",

        defaultValues:
            defaultFormValues

    });

    const selectedSectionIdValue =
        watch("sectionId");

    const selectedQuestionType =
        watch("questionType");

    const selectedSection =
        sections.find(
            (section) =>
                String(section.id) ===
                String(
                    selectedSectionIdValue
                )
        );

    const isCriticalSection =
        selectedSection
            ?.section_type ===
        "critical_violation";

    /*
     * Load active QA sections.
     */
    useEffect(() => {

        loadSections();

    }, []);

    /*
     * Automatically configure question
     * when a critical section is selected.
     */
    useEffect(() => {

        if (!isCriticalSection) {
            return;
        }

        setValue(
            "questionType",
            "critical_violation",
            {
                shouldValidate: true
            }
        );

        setValue(
            "maximumScore",
            0,
            {
                shouldValidate: true
            }
        );

        setValue(
            "isMandatory",
            true
        );

        setValue(
            "isCritical",
            true
        );

    }, [
        isCriticalSection,
        setValue
    ]);

    /*
     * Set a default score for normal
     * yes/no questions.
     */
    useEffect(() => {

        if (
            !isCriticalSection &&
            selectedQuestionType ===
            "yes_no"
        ) {

            const currentScore =
                Number(
                    watch(
                        "maximumScore"
                    )
                );

            if (
                !currentScore ||
                currentScore < 1
            ) {
                setValue(
                    "maximumScore",
                    5,
                    {
                        shouldValidate: true
                    }
                );
            }

        }

    }, [
        selectedQuestionType,
        isCriticalSection,
        setValue,
        watch
    ]);

    /*
     * Populate edit/view form.
     */
    useEffect(() => {

        if (
            (
                isEditMode ||
                isViewMode
            ) &&
            sections.length === 0
        ) {
            return;
        }

        if (
            (
                isEditMode ||
                isViewMode
            ) &&
            data
        ) {

            const currentSectionId =
                data?.section_id ??
                data?.section?.id ??
                "";

            const currentSection =
                sections.find(
                    (section) =>
                        String(section.id) ===
                        String(
                            currentSectionId
                        )
                );

            const criticalSection =
                currentSection
                    ?.section_type ===
                "critical_violation";

            reset({
                sectionId:
                    normalizeSectionId(
                        currentSectionId
                    ),

                questionText:
                    data?.question_text ||
                    "",

                questionType:
                    criticalSection
                        ? "critical_violation"
                        : (
                            data?.question_type ||
                            "yes_no"
                        ),

                maximumScore:
                    criticalSection
                        ? 0
                        : Number(
                            data?.maximum_score ??
                            0
                        ),

                displayOrder:
                    Number(
                        data?.display_order ??
                        1
                    ),

                status:
                    data?.status ||
                    "active",

                isMandatory:
                    criticalSection
                        ? true
                        : normalizeBoolean(
                            data?.is_mandatory
                        ),

                isCritical:
                    criticalSection
                        ? true
                        : normalizeBoolean(
                            data?.is_critical
                        )
            });

            return;

        }

        reset(
            defaultFormValues
        );

    }, [
        data,
        sections,
        isEditMode,
        isViewMode,
        defaultFormValues,
        reset
    ]);

    const loadSections =
        async () => {

            try {

                const response =
                    await getAllQASectionsApi({
                        page: 1,
                        limit: 100,
                        status: "",
                        search: ""
                    });

                if (
                    response?.data?.success
                ) {

                    const responseData =
                        response.data.data;

                    const sectionRows =
                        responseData?.sections ||
                        responseData?.rows ||
                        responseData?.data ||
                        responseData ||
                        [];

                    setSections(
                        Array.isArray(
                            sectionRows
                        )
                            ? sectionRows
                            : []
                    );

                    return;

                }

                ToastrService.error(
                    response?.error
                        ?.data
                        ?.message ||
                    "Unable to retrieve QA sections"
                );

            } catch (error) {

                console.error(
                    "Load QA sections error:",
                    error
                );

                ToastrService.error(
                    error?.message ||
                    "Unable to retrieve QA sections"
                );

            }

        };

    const getErrorMessage = (
        response,
        defaultMessage
    ) => {

        return (
            response?.error
                ?.data
                ?.message ||
            response?.data
                ?.message ||
            defaultMessage
        );

    };

    const resetForm = () => {

        if (
            isEditMode &&
            data
        ) {

            const currentSectionId =
                data?.section_id ??
                data?.section?.id ??
                "";

            const currentSection =
                sections.find(
                    (section) =>
                        String(section.id) ===
                        String(
                            currentSectionId
                        )
                );

            const criticalSection =
                currentSection
                    ?.section_type ===
                "critical_violation";

            reset({
                sectionId:
                    normalizeSectionId(
                        currentSectionId
                    ),

                questionText:
                    data?.question_text ||
                    "",

                questionType:
                    criticalSection
                        ? "critical_violation"
                        : (
                            data?.question_type ||
                            "yes_no"
                        ),

                maximumScore:
                    criticalSection
                        ? 0
                        : Number(
                            data?.maximum_score ??
                            0
                        ),

                displayOrder:
                    Number(
                        data?.display_order ??
                        1
                    ),

                status:
                    data?.status ||
                    "active",

                isMandatory:
                    criticalSection
                        ? true
                        : normalizeBoolean(
                            data?.is_mandatory
                        ),

                isCritical:
                    criticalSection
                        ? true
                        : normalizeBoolean(
                            data?.is_critical
                        )
            });

            return;

        }

        reset(
            defaultFormValues
        );

    };

    const onSubmit = async (
        values
    ) => {

        try {

            const currentSection =
                sections.find(
                    (section) =>
                        String(section.id) ===
                        String(
                            values.sectionId
                        )
                );

            if (!currentSection) {

                ToastrService.error(
                    "Selected QA section was not found"
                );

                return;

            }

            const criticalSection =
                currentSection
                    .section_type ===
                "critical_violation";

            const payload = {

                section_id:
                    Number(
                        values.sectionId
                    ),

                question_text:
                    values
                        .questionText
                        .trim(),

                question_type:
                    criticalSection
                        ? "critical_violation"
                        : values.questionType,

                maximum_score:
                    criticalSection
                        ? 0
                        : Number(
                            values.maximumScore
                        ),

                display_order:
                    Number(
                        values.displayOrder
                    ),

                status:
                    values.status,

                is_mandatory:
                    criticalSection
                        ? 1
                        : (
                            values.isMandatory
                                ? 1
                                : 0
                        ),

                is_critical:
                    criticalSection
                        ? 1
                        : (
                            values.isCritical
                                ? 1
                                : 0
                        ),

                options: []
            };

            let response;

            if (isEditMode) {

                if (!data?.id) {

                    ToastrService.error(
                        "Question ID is required"
                    );

                    return;

                }

                response =
                    await updateQAQuestionApi({
                        id: data.id,
                        ...payload
                    });

            } else {

                response =
                    await createQAQuestionApi(
                        payload
                    );

            }

            if (
                response?.data?.success
            ) {

                ToastrService.success(
                    response.data.message ||
                    (
                        isEditMode
                            ? "QA question updated successfully"
                            : "QA question created successfully"
                    )
                );

                onSave?.(
                    response?.data?.data
                );

                return;

            }

            ToastrService.error(
                getErrorMessage(
                    response,
                    isEditMode
                        ? "Unable to update QA question"
                        : "Unable to create QA question"
                )
            );

        } catch (error) {

            console.error(
                "QA question submit error:",
                error
            );

            ToastrService.error(
                error?.data?.message ||
                error?.message ||
                "Something went wrong"
            );

        }

    };


    const sectionOptions = sections.map(
        (section) => {

            const isInactive = String(section.status).toLowerCase() === "inactive";

            return {
                value: String(section.id),
                label: section.section_name + (section.section_type === "critical_violation" ? " — Critical" : ""),
                isDisabled: isInactive
            };
        }
    );

    const selectedSectionOption = (() => {
        const selectedId = watch("sectionId");
        return (sectionOptions.find((option) => String(option.value) === String(selectedId)) || null);
    })();

    const drawerTitle = () => {

        if (isViewMode) {
            return "View QA Question";
        }

        if (isEditMode) {
            return "Edit QA Question";
        }

        return "Add QA Question";

    };

    const drawerDescription = () => {

        if (isViewMode) {
            return "View the selected QA question details.";
        }

        if (isEditMode) {
            return "Update the selected QA question.";
        }

        return "Add a question under an evaluation section.";

    };

    return (
        <div>

            {
                isLoading && (
                    <Loader />
                )
            }

            <div
                className="offcanvas offcanvas-end show"
                tabIndex="-1"
                style={{
                    visibility: "visible",
                    width: "600px",
                    maxWidth: "100%"
                }}
                aria-labelledby="qaQuestionDrawer"
            >

                <div className="offcanvas-header px-4 py-3 border-bottom">

                    <div>

                        <h4
                            id="qaQuestionDrawer"
                            className="mb-1 drawer-header"
                        >
                            {
                                drawerTitle()
                            }
                        </h4>

                        <small className="text-muted">
                            {
                                drawerDescription()
                            }
                        </small>

                    </div>

                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        disabled={
                            isSubmitting
                        }
                        onClick={
                            onClose
                        }
                    />

                </div>

                <div className="offcanvas-body px-4 py-3">

                    <form
                        noValidate
                        autoComplete="off"
                        onSubmit={
                            handleSubmit(
                                onSubmit
                            )
                        }
                    >

                        {/* Evaluation section */}

                        <div className="mb-3">

                            <label For="sectionId" className="form-label">
                                Evaluation Section <span className="text-danger ms-1">*</span>
                            </label>

                            <Select
                                inputId="sectionId"

                                value={selectedSectionOption}

                                options={sectionOptions}

                                isOptionDisabled={(option) => option.isDisabled}

                                isDisabled={isViewMode || isSubmitting}

                                placeholder="Select section"

                                isClearable={false}

                                onChange={
                                    (selectedOption) => {

                                        setValue(
                                            "sectionId", selectedOption ? selectedOption.value : "",
                                            {
                                                shouldValidate: true,
                                                shouldDirty: true
                                            }
                                        );

                                    }
                                }

                                formatOptionLabel={
                                    (option) => {

                                        const isInactive = option.isDisabled;

                                        if (isInactive) {
                                            return (
                                                <span
                                                    style={{
                                                        color: "#6c757d"
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            textDecoration: "line-through"
                                                        }}
                                                    >
                                                        {option.label}
                                                    </span>

                                                    {/* <span
                                                        style={{
                                                            marginLeft: "8px"
                                                        }}
                                                    >
                                                        (Inactive)
                                                    </span> */}
                                                </span>
                                            );
                                        }

                                        return (
                                            <span>
                                                {option.label}
                                            </span>
                                        );
                                    }
                                }

                                styles={{
                                    option: (base, state) => {

                                        if (
                                            state.isDisabled
                                        ) {
                                            return {
                                                ...base,
                                                color: "#6c757d",
                                                backgroundColor: "transparent",
                                                cursor: "not-allowed"
                                            };
                                        }

                                        return base;
                                    },

                                    singleValue: (base) => {

                                        const selectedOption = sectionOptions.find((option) => String(option.value) === String(watch("sectionId")));

                                        const isInactive = selectedOption?.isDisabled;

                                        return {
                                            ...base,
                                            color: isInactive ? "#6c757d" : base.color,
                                            textDecoration: isInactive ? "line-through" : "none"
                                        };
                                    }
                                }}

                                classNamePrefix="react-select"
                            />

                            {
                                errors.sectionId && (
                                    <div className="invalid-feedback d-block">
                                        {
                                            errors.sectionId.message
                                        }
                                    </div>
                                )
                            }

                        </div>

                        {/* <div className="mb-3">

                            <label
                                htmlFor="sectionId"
                                className="form-label"
                            >
                                Evaluation Section

                                <span className="text-danger ms-1">
                                    *
                                </span>
                            </label>

                            <select
                                id="sectionId"
                                className={`form-select ${
                                    errors.sectionId
                                        ? "is-invalid"
                                        : ""
                                }`}
                                disabled={
                                    isViewMode ||
                                    isSubmitting
                                }
                                {...register(
                                    "sectionId"
                                )}
                            >
                                <option value="">
                                    Select section
                                </option>

                                {
                                    sections.map(
                                        (
                                            section
                                        ) => (
                                            <option
                                                key={
                                                    section.id
                                                }
                                                value={
                                                    String(
                                                        section.id
                                                    )
                                                }
                                            >
                                                {
                                                    section.section_name
                                                }

                                                {
                                                    section.section_type ===
                                                    "critical_violation"
                                                        ? " — Critical"
                                                        : ""
                                                }
                                            </option>
                                        )
                                    )
                                }

                            </select>

                            {
                                errors.sectionId && (
                                    <div className="invalid-feedback">
                                        {
                                            errors.sectionId
                                                .message
                                        }
                                    </div>
                                )
                            }

                        </div> */}

                        {/* Critical section notice */}

                        {
                            isCriticalSection && (
                                <div className="alert alert-danger border mb-3">

                                    <div className="fw-semibold">
                                        Critical Violation Question
                                    </div>

                                    <div className="small mt-1">
                                        This question does not carry a score.
                                    </div>

                                    <div className="small">
                                        If the evaluator marks the violation as occurred, the evaluation can result in a critical failure.
                                    </div>

                                </div>
                            )
                        }

                        {/* Question */}

                        <div className="mb-3">

                            <label
                                htmlFor="questionText"
                                className="form-label"
                            >
                                Question

                                <span className="text-danger ms-1">
                                    *
                                </span>
                            </label>

                            <textarea
                                id="questionText"
                                rows="4"
                                placeholder={
                                    isCriticalSection
                                        ? "Example: Data privacy breach or sharing sensitive information"
                                        : "Example: Did the agent greet the customer professionally?"
                                }
                                className={`form-control ${errors.questionText
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                disabled={
                                    isViewMode ||
                                    isSubmitting
                                }
                                {...register(
                                    "questionText"
                                )}
                            />

                            {
                                errors.questionText && (
                                    <div className="invalid-feedback">
                                        {
                                            errors.questionText
                                                .message
                                        }
                                    </div>
                                )
                            }

                        </div>

                        <div className="row">

                            {/* Question type */}

                            <div className="col-md-6">

                                <div className="mb-3">

                                    <label
                                        htmlFor="questionType"
                                        className="form-label"
                                    >
                                        Question Type

                                        <span className="text-danger ms-1">
                                            *
                                        </span>
                                    </label>

                                    <select
                                        id="questionType"
                                        className={`form-select ${errors.questionType
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        disabled={
                                            isViewMode ||
                                            isSubmitting ||
                                            isCriticalSection
                                        }
                                        {...register(
                                            "questionType"
                                        )}
                                    >
                                        {
                                            questionTypes.map(
                                                (
                                                    type
                                                ) => (
                                                    <option
                                                        key={
                                                            type.value
                                                        }
                                                        value={
                                                            type.value
                                                        }
                                                    >
                                                        {
                                                            type.label
                                                        }
                                                    </option>
                                                )
                                            )
                                        }

                                    </select>

                                    {
                                        errors.questionType && (
                                            <div className="invalid-feedback">
                                                {
                                                    errors.questionType
                                                        .message
                                                }
                                            </div>
                                        )
                                    }

                                </div>

                            </div>

                            {/* Maximum score */}

                            <div className="col-md-6">

                                <div className="mb-3">

                                    <label
                                        htmlFor="maximumScore"
                                        className="form-label"
                                    >
                                        Maximum Score

                                        <span className="text-danger ms-1">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="number"
                                        id="maximumScore"
                                        min={
                                            isCriticalSection
                                                ? 0
                                                : 1
                                        }
                                        max="100"
                                        step="1"
                                        placeholder={
                                            isCriticalSection
                                                ? "0"
                                                : "Example: 5"
                                        }
                                        className={`form-control ${errors.maximumScore
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        disabled={
                                            isViewMode ||
                                            isSubmitting ||
                                            isCriticalSection
                                        }
                                        {...register(
                                            "maximumScore"
                                        )}
                                    />

                                    {
                                        errors.maximumScore && (
                                            <div className="invalid-feedback">
                                                {
                                                    errors.maximumScore
                                                        .message
                                                }
                                            </div>
                                        )
                                    }

                                    {
                                        isCriticalSection && (
                                            <small className="text-muted">
                                                Critical violation questions always have a score of 0.
                                            </small>
                                        )
                                    }

                                </div>

                            </div>

                        </div>

                        <div className="row">

                            {/* Display order */}

                            <div className="col-md-6">

                                <div className="mb-3">

                                    <label
                                        htmlFor="displayOrder"
                                        className="form-label"
                                    >
                                        Display Order

                                        <span className="text-danger ms-1">
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="number"
                                        id="displayOrder"
                                        min="1"
                                        step="1"
                                        placeholder="Example: 1"
                                        className={`form-control ${errors.displayOrder
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        disabled={
                                            isViewMode ||
                                            isSubmitting
                                        }
                                        {...register(
                                            "displayOrder"
                                        )}
                                    />

                                    {
                                        errors.displayOrder && (
                                            <div className="invalid-feedback">
                                                {
                                                    errors.displayOrder
                                                        .message
                                                }
                                            </div>
                                        )
                                    }

                                </div>

                            </div>

                            {/* Status */}

                            <div className="col-md-6">

                                <div className="mb-3">

                                    <label
                                        htmlFor="status"
                                        className="form-label"
                                    >
                                        Status

                                        <span className="text-danger ms-1">
                                            *
                                        </span>
                                    </label>

                                    <select
                                        id="status"
                                        className={`form-select ${errors.status
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                        disabled={
                                            isViewMode ||
                                            isSubmitting
                                        }
                                        {...register(
                                            "status"
                                        )}
                                    >
                                        <option value="active">
                                            Active
                                        </option>

                                        <option value="inactive">
                                            Inactive
                                        </option>

                                    </select>

                                    {
                                        errors.status && (
                                            <div className="invalid-feedback">
                                                {
                                                    errors.status
                                                        .message
                                                }
                                            </div>
                                        )
                                    }

                                </div>

                            </div>

                        </div>

                        {/* Mandatory and Critical */}

                        <div className="border rounded p-3 mb-3">

                            <div className="form-check mb-3">

                                <input
                                    type="checkbox"
                                    id="isMandatory"
                                    className="form-check-input"
                                    disabled={
                                        isViewMode ||
                                        isSubmitting ||
                                        isCriticalSection
                                    }
                                    {...register(
                                        "isMandatory"
                                    )}
                                />

                                <label
                                    htmlFor="isMandatory"
                                    className="form-check-label fw-semibold custom-checkbox-label"
                                >
                                    Mandatory Question
                                </label>

                                <div className="small text-muted">
                                    The evaluator must answer this question before submitting the evaluation.
                                </div>

                            </div>

                            <div className="form-check">

                                <input
                                    type="checkbox"
                                    id="isCritical"
                                    className="form-check-input"
                                    disabled={
                                        isViewMode ||
                                        isSubmitting ||
                                        isCriticalSection
                                    }
                                    {...register(
                                        "isCritical"
                                    )}
                                />

                                <label
                                    htmlFor="isCritical"
                                    className="form-check-label fw-semibold custom-checkbox-label"
                                >
                                    Critical Question
                                </label>

                                <div className="small text-muted">
                                    A triggered critical violation can override the normal QA result.
                                </div>

                            </div>

                        </div>

                        {/* Help information */}

                        <div
                            className={`alert ${isCriticalSection
                                ? "alert-danger"
                                : "alert-light"
                                } border`}
                        >

                            <strong className="small">
                                {
                                    isCriticalSection
                                        ? "Critical violation behaviour"
                                        : "Question configuration"
                                }
                            </strong>

                            {
                                isCriticalSection
                                    ? (
                                        <>
                                            <div className="small mt-1">
                                                Evaluation answer options should be Occurred and Not Occurred.
                                            </div>

                                            <div className="small">
                                                Occurred triggers the critical-failure rule.
                                            </div>
                                        </>
                                    )
                                    : (
                                        <>
                                            <div className="small text-muted mt-1">
                                                Maximum score represents the highest score available for this question.
                                            </div>

                                            <div className="small text-muted">
                                                Display order controls the question’s position inside the section.
                                            </div>
                                        </>
                                    )
                            }

                        </div>

                        {/* Actions */}

                        <div className="d-flex gap-2 mt-4">

                            {
                                !isViewMode && (
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={
                                            isSubmitting
                                        }
                                    >
                                        {
                                            isSubmitting
                                                ? (
                                                    isEditMode
                                                        ? "Updating..."
                                                        : "Saving..."
                                                )
                                                : (
                                                    isEditMode
                                                        ? "Update Question"
                                                        : "Save Question"
                                                )
                                        }
                                    </button>
                                )
                            }

                            {
                                !isViewMode && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        disabled={
                                            isSubmitting
                                        }
                                        onClick={
                                            resetForm
                                        }
                                    >
                                        Reset
                                    </button>
                                )
                            }

                            <button
                                type="button"
                                className="btn btn-light"
                                disabled={
                                    isSubmitting
                                }
                                onClick={
                                    onClose
                                }
                            >
                                {
                                    isViewMode
                                        ? "Close"
                                        : "Cancel"
                                }
                            </button>

                        </div>

                    </form>

                </div>

            </div>

            <div
                className="offcanvas-backdrop fade show"
                onClick={
                    isSubmitting
                        ? undefined
                        : onClose
                }
            />

        </div>
    );

};

export default AddQAQuestion;